import { useAuth } from "@/context/auth/AuthContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import TextField from "../form-fields/TextField";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import {updateFileById} from "@/api/file.ts";

const schema = z.object({
  title: z.string().min(1, { message: "Le nom du fichier est requis" }),
  description: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

interface EditFileModalProps {
  id: string | undefined;
  isOpen: boolean;
  fileData?: any;
  onClose?: () => void;
}

const EditFileModal = ({
                         id,
                         isOpen = false,
                         fileData,
                         onClose = () => {}
                       }: EditFileModalProps) => {
  const [open, setOpen] = useState(isOpen);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { instanceData } = useAuth();
  const { t } = useTranslation();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      description: "",
    }
  });

  const { control, getValues, reset, formState } = form;

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && fileData) {
      reset({
        title: fileData.title || fileData.sourceName || "",
        description: fileData.description || "",
      });
    } else if (isOpen) {
      reset({
        title: "",
        description: "",
      });
    }
  }, [isOpen, fileData, reset]);

  const onSubmit = async (data: SchemaType) => {
    if (!id) {
      console.error("No ID provided for document update");
      return;
    }

    if (instanceData == null) {
      console.error("Tried to update a Document, but instanceData is null");
      return;
    }
    setIsSubmitting(true);

    try {
      await updateFileById(id, {
        title: data.title,
        description: data.description
      }
      );

      toast({
        description: t("fileUpdatedSuccessfully"),
        variant: "success",
      });

      handleClose();
    } catch (error) {
      console.error("Error updating document:", error);
      toast({
        description: t("success"),
        variant: "success",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[475px] bg-white p-0">
          <DialogHeader className="border-b px-6 py-6">
            <DialogTitle>
              { t("editFileInformations") }
            </DialogTitle>
          </DialogHeader>
          <FormProvider {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4 px-6 pb-6 pt-4"
            >
              <TextField
                config={{
                  name: "title",
                  type: "text",
                  label: t("uploadFileName"),
                }}
                control={control}
                getValues={getValues}
              />
              <TextField
                config={{
                  name: "description",
                  type: "text",
                  label: t("documentsDescription"),
                }}
                control={control}
                getValues={getValues}
              />
            </form>
          </FormProvider>

          <DialogFooter className="flex justify-end gap-2 border-t pt-4 pb-4 px-6">
            <Button
              className="w-full"
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t("cancel")}
            </Button>
            <Button
              className="w-full"
              type="submit"
              onClick={form.handleSubmit(onSubmit)}
              disabled={!formState.isValid || isSubmitting}
            >
              { t("update") }
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
};

export default EditFileModal;
